import React, { useState, useEffect, useRef } from 'react';
import { BillingRow } from './billing-tabel';
import { apiGet, apiPost, getApiUrl } from '../utils/api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface BillingSummaryProps {
  rows: BillingRow[];
  customerName: string;
  phoneNumber: string;
  paymentMode: string;
  amountPaid: number;
  patientId?: number;
  onSubmitSuccess?: (message: string) => void;
  onSubmitError?: (message: string) => void;
  onDone?: () => void;
  globalDiscount?: number;
  onGlobalDiscountChange?: (value: number) => void;
}

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to convert number to words (Indian numbering system)
const numberToWords = (num: number): string => {
  const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
  const scales = ['', 'THOUSAND', 'LAKH', 'CRORE'];

  if (num === 0) return 'ZERO';
  if (num < 0) return 'MINUS ' + numberToWords(Math.abs(num));

  let words = '';

  // Handle rupees and paise
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);

  if (rupees > 0) {
    // Convert rupees part
    let remaining = rupees;
    let scaleIndex = 0;

    while (remaining > 0) {
      const chunk = remaining % 1000;
      if (chunk > 0) {
        let chunkWords = '';
        const hundreds = Math.floor(chunk / 100);
        const remainder = chunk % 100;

        if (hundreds > 0) {
          chunkWords += ones[hundreds] + ' HUNDRED';
          if (remainder > 0) chunkWords += ' ';
        }

        if (remainder > 0) {
          if (remainder < 20) {
            chunkWords += ones[remainder];
          } else {
            const ten = Math.floor(remainder / 10);
            const one = remainder % 10;
            chunkWords += tens[ten];
            if (one > 0) chunkWords += ' ' + ones[one];
          }
        }

        if (scaleIndex > 0 && chunkWords) {
          chunkWords += ' ' + scales[scaleIndex];
        }

        words = chunkWords + (words ? ' ' + words : '');
      }

      remaining = Math.floor(remaining / 1000);
      scaleIndex++;
    }
  } else {
    words = 'ZERO';
  }

  words += ' RUPEES';
  if (paise > 0) {
    words += ' AND ';
    if (paise < 20) {
      words += ones[paise];
    } else {
      const ten = Math.floor(paise / 10);
      const one = paise % 10;
      words += tens[ten];
      if (one > 0) words += ' ' + ones[one];
    }
    words += ' PAISE';
  }

  return words;
};

// Helper function to format expiry date
const formatExpiryDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]}-${date.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

const formatBillDate = (dateValue?: string): string => {
  const fallback = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  if (!dateValue) return fallback;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Helper to normalize quantity type values (matching billing-tabel.tsx)
const normalizeQtyType = (qtyType?: string): string => {
  const value = (qtyType || '').toLowerCase();
  switch (value) {
    case 'strip':
    case 'strips':
      return 'Strip';
    case 'bottle':
    case 'bottles':
      return 'Bottles';
    case 'tablet':
    case 'tablets':
    case 'individual':
      return 'Tablet';
    case 'box':
    case 'boxes':
      return 'Box';
    case 'tube':
    case 'tubes':
      return 'Tube';
    default:
      return 'Strip';
  }
};

const BillingSummary: React.FC<BillingSummaryProps> = ({
  rows,
  customerName,
  phoneNumber,
  paymentMode,
  amountPaid,
  patientId,
  onSubmitSuccess,
  onSubmitError,
  onDone,
  globalDiscount,
  onGlobalDiscountChange
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [showResponseMessage, setShowResponseMessage] = useState(false);
  const [showBillPopup, setShowBillPopup] = useState(false);
  const [billData, setBillData] = useState<any>(null);
  const [isSendingToWhatsApp, setIsSendingToWhatsApp] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const whatsAppPendingBlobRef = useRef<Blob | null>(null);
  const [internalWholeBillDiscount, setInternalWholeBillDiscount] = useState<number>(0);
  const wholeBillDiscount = globalDiscount !== undefined ? globalDiscount : internalWholeBillDiscount;
  const [wholeBillDiscountInput, setWholeBillDiscountInput] = useState<string>(() =>
    wholeBillDiscount === 0 ? '' : wholeBillDiscount.toString()
  );
  const [wholeBillDiscountRupeeInput, setWholeBillDiscountRupeeInput] = useState<string>('');
  const [isEditingWholeBillDiscountPercent, setIsEditingWholeBillDiscountPercent] = useState(false);
  const [isEditingWholeBillDiscountRupee, setIsEditingWholeBillDiscountRupee] = useState(false);
  const [clinicInfo, setClinicInfo] = useState<{
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstNumber?: string;
  } | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [gstin, setGstin] = useState<string | null>(null);
  const [dlNo, setDlNo] = useState<string | null>(null);
  const handleWholeBillDiscountChange = (value: number) => {
    if (onGlobalDiscountChange) {
      onGlobalDiscountChange(value);
    }
    if (globalDiscount === undefined) {
      setInternalWholeBillDiscount(value);
    }
  };
  const billPopupContentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (globalDiscount !== undefined) {
      setInternalWholeBillDiscount(globalDiscount);
    }
  }, [globalDiscount]);
  useEffect(() => {
    if (!isEditingWholeBillDiscountPercent) {
      const normalizedPercent = Math.min(Math.max(Math.round(wholeBillDiscount || 0), 0), 100);
      setWholeBillDiscountInput(normalizedPercent === 0 ? '' : normalizedPercent.toString());
    }
  }, [wholeBillDiscount, isEditingWholeBillDiscountPercent]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load clinic / pharmacy details from localStorage for bill header
  useEffect(() => {
    try {
      const stored = localStorage.getItem('clinicDetails') || localStorage.getItem('pharmacyDetails');
      if (stored) {
        const parsed = JSON.parse(stored);
        setClinicInfo({
          name: parsed.name || parsed.clinic_name || parsed.pharmacy_name,
          address: parsed.address || parsed.clinic_address || parsed.pharmacy_address,
          phone: parsed.phone || parsed.mobile || parsed.contact_number,
          email: parsed.email || parsed.mail,
          gstNumber: parsed.gstNumber || parsed.gst_no || parsed.gst || parsed.gstin,
        });
      }
    } catch (e) {
      console.error('Failed to load clinic details from localStorage', e);
    }
  }, []);

  // Load full_name from localStorage for bill display
  useEffect(() => {
    try {
      const storedFullName = localStorage.getItem('full_name');
      if (storedFullName) {
        setFullName(storedFullName);
      }
    } catch (e) {
      console.error('Failed to load full_name from localStorage', e);
    }
  }, []);

  // Load GSTIN from localStorage for bill display
  useEffect(() => {
    try {
      const storedGstin = localStorage.getItem('gstin');
      if (storedGstin) {
        setGstin(storedGstin);
      }
    } catch (e) {
      console.error('Failed to load gstin from localStorage', e);
    }
  }, []);

  // Load DL NO from localStorage for bill display
  useEffect(() => {
    try {
      const storedDlNo = localStorage.getItem('dl_no');
      if (storedDlNo) {
        setDlNo(storedDlNo);
      }
    } catch (e) {
      console.error('Failed to load dl_no from localStorage', e);
    }
  }, []);

  // Calculate Subtotal (sum of base amounts before discount and GST)
  // Uses same logic as billing-tabel.tsx to handle "Tablet" quantity type
  const calculateSubtotal = (): number => {
    return rows.reduce((sum, row) => {
      const sellingPrice = row['Selling Price'] || 0;
      const quantity = row['Quantity'] || 0;
      const qtyType = normalizeQtyType(row['Qty Type']);
      
      // For Tablet type, use the same calculation as billing-tabel.tsx
      if (qtyType === 'Tablet') {
        const tabletsPerStrip = row['Tablets/Strip'] || 0;
        const divisor = tabletsPerStrip > 0 ? tabletsPerStrip : 1;
        // For Tablet, the base calculation matches billing-tabel.tsx logic
        // But we still need subtotal for display, so use standard calculation
        const base = sellingPrice * quantity;
        return sum + base;
      }
      
      // Standard calculation for other quantity types
      const base = sellingPrice * quantity;
      return sum + base;
    }, 0);
  };

  // Calculate total Discount amount
  // Uses same logic as billing-tabel.tsx to handle "Tablet" quantity type
  const calculateTotalDiscount = (): number => {
    return rows.reduce((sum, row) => {
      const sellingPrice = row['Selling Price'] || 0;
      const quantity = row['Quantity'] || 0;
      const discount = row['Discount'] || 0;
      const qtyType = normalizeQtyType(row['Qty Type']);
      
      // For Tablet type, discount is not applied in the standard way
      // The Total is calculated as (quantity * sellingPrice) / tabletsPerStrip
      // So we don't apply discount for Tablet type in breakdown
      if (qtyType === 'Tablet') {
        return sum; // No discount applied for Tablet type
      }
      
      // Standard calculation for other quantity types
      const subtotal = sellingPrice * quantity;
      const discountAmount = (subtotal * discount) / 100;
      return sum + discountAmount;
    }, 0);
  };

  // Calculate total GST amount
  // Uses same logic as billing-tabel.tsx to handle "Tablet" quantity type
  const calculateTotalGST = (): number => {
    return rows.reduce((sum, row) => {
      const sellingPrice = row['Selling Price'] || 0;
      const quantity = row['Quantity'] || 0;
      const discount = row['Discount'] || 0;
      const gstRate = row['GST%'] || 0;
      const qtyType = normalizeQtyType(row['Qty Type']);
      
      // For Tablet type, GST is not applied in the standard way
      // The Total is calculated as (quantity * sellingPrice) / tabletsPerStrip
      // So we don't apply GST for Tablet type in breakdown
      if (qtyType === 'Tablet') {
        return sum; // No GST applied for Tablet type
      }
      
      // Standard calculation for other quantity types
      const subtotal = sellingPrice * quantity;
      const discountAmount = (subtotal * discount) / 100;
      const afterDiscount = subtotal - discountAmount;
      const gstAmount = (afterDiscount * gstRate) / 100;
      return sum + gstAmount;
    }, 0);
  };

  // Calculate Total Amount (sum of all row line totals from billing-tabel.tsx)
  // This should match calculateTotalValue() in billing-tabel.tsx exactly
  // Both sum row['Total'] which is calculated per row in billing-tabel.tsx
  const calculateTotalAmount = (): number => {
    // Use the Total field from each row (calculated in billing-tabel.tsx)
    // This matches: rows.reduce((sum, row) => sum + (row['Total'] || 0), 0) in billing-tabel.tsx
    return rows.reduce((sum, row) => sum + (row['Total'] || 0), 0);
  };

  const subtotal = calculateSubtotal();
  const totalDiscount = calculateTotalDiscount();
  const totalGST = calculateTotalGST();
  const totalAmount = calculateTotalAmount(); // This should match "Total Value" in billing-tabel.tsx
  
  // Calculate whole bill discount amount (applied on totalAmount from billing-tabel.tsx)
  const wholeBillDiscountAmount = (totalAmount * wholeBillDiscount) / 100;
  
  // Grand total = sum of row totals minus whole‑bill discount, rounded up to next whole rupee (e.g. 157.50 → 158)
  const grandTotalBeforeRound = totalAmount - wholeBillDiscountAmount;
  const grandTotalUnrounded = Math.round(grandTotalBeforeRound * 100) / 100;
  const grandTotal = Math.ceil(grandTotalUnrounded);

  useEffect(() => {
    if (!isEditingWholeBillDiscountRupee) {
      const rupeeDiscount = totalAmount > 0 ? (totalAmount * wholeBillDiscount) / 100 : 0;
      setWholeBillDiscountRupeeInput(rupeeDiscount === 0 ? '' : rupeeDiscount.toFixed(2));
    }
  }, [wholeBillDiscount, totalAmount]);

  // Treat amount as "entered" only when it's a positive number.
  // This is used to disable the submit button when the user hasn't entered any amount.
  const isAmountEntered = typeof amountPaid === 'number' && amountPaid > 0;

  // Single source of truth for when the Submit / Generate Bill button should be disabled
  const isSubmitDisabled = isSubmitting || (rows.length === 0 && !billData) || (!billData && !isAmountEntered);

  // Handle Submit Sale
  const handleSubmitSale = async () => {
    console.log('Submit Sale button clicked');
    console.log('Current state:', { 
      rowsCount: rows.length, 
      customerName, 
      amountPaid, 
      grandTotal, 
      isSubmitting 
    });
    
    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      console.log('Auth token check:', authToken ? 'Found' : 'Not found');
      
      if (!authToken) {
        console.error('No auth token found');
        if (onSubmitError) {
          onSubmitError('Authentication token not found. Please log in again.');
        } else {
          alert('Authentication token not found. Please log in again.');
        }
        return;
      }

      // Validate customer information
      console.log('Validating customer name:', customerName);
      if (!customerName.trim()) {
        console.error('Customer name is empty');
        if (onSubmitError) {
          onSubmitError('Please enter customer name.');
        } else {
          alert('Please enter customer name.');
        }
        return;
      }

      // Phone number is optional, but if provided, it should be valid
      // No validation needed here as it's optional

      // Filter out empty rows and prepare data for API
      console.log('Filtering valid rows from total rows:', rows.length);
      const validRows = rows.filter(row => 
        row['Medicine Name'].trim() !== '' && 
        row['Batch No'].trim() !== '' &&
        row['Quantity'] > 0 &&
        row['Selling Price'] > 0
      );
      console.log('Valid rows count:', validRows.length);

      if (validRows.length === 0) {
        console.error('No valid rows found');
        if (onSubmitError) {
          onSubmitError('Please add at least one medicine item.');
        } else {
          alert('Please add at least one medicine item.');
        }
        return;
      }

      console.log('Setting isSubmitting to true');
      setIsSubmitting(true);

      // Prepare items for API according to format
      const items = validRows.map(row => {
        const quantity = typeof row['Quantity'] === 'number' ? row['Quantity'] : parseFloat(String(row['Quantity'])) || 0;
        
        // Map quantity type from billing-tabel format to API format
        // billing-tabel uses: 'Strip', 'Bottles', 'Tablet', 'Box', 'Tube'
        // API expects: 'strip', 'bottle', 'individual', 'box', 'tube'
        // Note: 'Tablet' type should be sent as 'individual' in the API
        const qtyTypeMap: { [key: string]: string } = {
          'Strip': 'strip',
          'Bottles': 'bottle',
          'Bottle': 'bottle',
          'Tablet': 'individual',
          'Box': 'box',
          'Tube': 'tube',
          'Individual': 'individual'
        };
        const rawQtyType = row['Qty Type'] || 'Strip';
        const quantityType = qtyTypeMap[rawQtyType] || rawQtyType.toLowerCase();
        
        const displaySellingPrice = typeof row['Selling Price'] === 'number'
          ? row['Selling Price']
          : parseFloat(String(row['Selling Price'])) || 0;
        const tabletsPerStrip = typeof row['Tablets/Strip'] === 'number'
          ? row['Tablets/Strip']
          : parseFloat(String(row['Tablets/Strip'])) || 0;
        const stripBaseSellingPrice = typeof row.strip_base_selling_price === 'number'
          ? row.strip_base_selling_price
          : parseFloat(String(row.strip_base_selling_price || 0)) || 0;
        const sellingPrice = quantityType === 'individual'
          ? (
              stripBaseSellingPrice > 0
                ? stripBaseSellingPrice
                : (tabletsPerStrip > 0 ? displaySellingPrice * tabletsPerStrip : displaySellingPrice)
            )
          : displaySellingPrice;
        
        // Ensure GST is always a valid number
        const gstValue = row['GST%'];
        const gstRate = (gstValue !== undefined && gstValue !== null && !isNaN(Number(gstValue)))
          ? parseFloat(String(gstValue))
          : 0.0;
        
        // Ensure discount is always a valid number
        const discountValue = row['Discount'];
        const discountPercent = (discountValue !== undefined && discountValue !== null && !isNaN(Number(discountValue)))
          ? parseFloat(String(discountValue))
          : 0.0;
        
        const itemData: any = {
          medicine_name: row['Medicine Name'].trim(),
          batch_no: row['Batch No'].trim(),
          quantity: quantity,
          quantity_type: quantityType,
          discount_percent: discountPercent,
          gst_percent: gstRate,
          selling_price: sellingPrice
        };

        // Add tablets_per_strip only for strip type (matching Checkout-billing.tsx)
        if (quantityType === 'strip' && row['Tablets/Strip']) {
          itemData.tablets_per_strip = tabletsPerStrip;
        }

        return itemData;
      });

      // Payment mode - use as-is if already lowercase, otherwise convert (matching Checkout-billing.tsx)
      // Checkout-billing uses salesForm.paymentMode directly which is already lowercase
      const paymentModeValue = paymentMode.trim() || 'cash';
      const normalizedPaymentMode = paymentModeValue.toLowerCase();
      
      // Prepare request body according to API specification (exactly matching Checkout-billing.tsx)
      const requestBody = {
        customer_name: customerName.trim(),
        customer_phone: phoneNumber.trim() || '', // Send empty string if not provided (optional field)
        amount_paid: parseFloat(String(amountPaid)) || 0,
        payment_mode: normalizedPaymentMode,
        items: items
      };

      console.log('Submitting sale with data:', requestBody);

      const response = await apiPost('sales/', requestBody);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to submit sale:', response.status, errorText);
        console.error('Request body that failed:', JSON.stringify(requestBody, null, 2));
        
        let errorMessage = errorText;
        let errorCode = '';
        let batchNumber = '';
        let medicineName = '';
        
        try {
          const errorJson = JSON.parse(errorText);
          const rawDetail = errorJson?.detail;
          errorMessage = typeof rawDetail === 'string'
            ? rawDetail
            : (errorJson.error || errorJson.message || errorText);
          errorCode = errorJson?.code || '';

          // Handle structured detail payloads, e.g.
          // { detail: { code, message, meta: { ... } } }
          if (rawDetail && typeof rawDetail === 'object' && !Array.isArray(rawDetail)) {
            const detailObj = rawDetail as {
              code?: string;
              message?: string;
              meta?: Record<string, any>;
            };
            errorCode = detailObj.code || errorCode;
            if (detailObj.message) {
              errorMessage = detailObj.message;
            }

            const meta = detailObj.meta || {};
            if (meta.batch_no || meta.batch_number || meta.batch) {
              batchNumber = meta.batch_no || meta.batch_number || meta.batch;
            }
            if (meta.medicine_name || meta.medicine || meta.medicineName) {
              medicineName = meta.medicine_name || meta.medicine || meta.medicineName;
            }
          }
          
          // Handle array of errors (some APIs return errors as an array)
          if (Array.isArray(errorJson)) {
            errorMessage = errorJson.map((err: any) => 
              typeof err === 'string' ? err : (err.detail || err.error || err.message || JSON.stringify(err))
            ).join('; ');
          }
          
          // Also check if batch info is in the error object itself (highest priority)
          if (errorJson.batch_no || errorJson.batch_number || errorJson.batch) {
            batchNumber = errorJson.batch_no || errorJson.batch_number || errorJson.batch;
          }
          if (errorJson.medicine_name || errorJson.medicine || errorJson.medicineName) {
            medicineName = errorJson.medicine_name || errorJson.medicine || errorJson.medicineName;
          }
          
          // Try to extract batch number and medicine name from error message string
          // Check if error mentions batch or batch-related issues
          if (!batchNumber) {
            const batchMatch = errorMessage.match(/batch\s+([A-Z0-9]+)/i);
            const batchMatch2 = errorMessage.match(/Batch\s+([A-Z0-9]+)/i);
            const batchMatch3 = errorMessage.match(/\bB(\d+)\b/i);
            
            if (batchMatch) {
              batchNumber = batchMatch[1];
            } else if (batchMatch2) {
              batchNumber = batchMatch2[1];
            } else if (batchMatch3) {
              batchNumber = 'B' + batchMatch3[1];
            }
          }
          
          // Try to extract medicine name from error message
          if (!medicineName) {
            const medicineMatch = errorMessage.match(/for\s+([A-Za-z0-9\s\-]+?)(?:\s+not|\s+expired|$)/i);
            if (medicineMatch) {
              medicineName = medicineMatch[1].trim();
            }
          }
          
          // If we have batch info but no medicine name, try to find it from the request
          if (batchNumber && !medicineName) {
            // Find the medicine from request items that matches the batch
            const matchingItem = items.find((item: any) => 
              item.batch_no === batchNumber || 
              item.batch_no?.toUpperCase() === batchNumber.toUpperCase() ||
              item.batch_no?.replace(/\s/g, '') === batchNumber.replace(/\s/g, '')
            );
            if (matchingItem) {
              medicineName = matchingItem.medicine_name;
            }
          }
          
          // Format user-friendly error message for batch errors
          if (batchNumber && (errorMessage.toLowerCase().includes('not found') || 
              errorMessage.toLowerCase().includes('expired') ||
              errorMessage.toLowerCase().includes('batch') ||
              errorMessage.toLowerCase().includes('invalid') ||
              errorMessage.toLowerCase().includes('does not exist'))) {
            if (medicineName) {
              errorMessage = `Batch ${batchNumber} for ${medicineName} not found or expired.`;
            } else {
              errorMessage = `Batch ${batchNumber} not found or expired.`;
            }
          }
        } catch (e) {
          // If not JSON, try to parse batch info from plain text error
          const batchMatch = errorText.match(/batch\s+([A-Z0-9]+)/i);
          const batchMatch2 = errorText.match(/Batch\s+([A-Z0-9]+)/i);
          const batchMatch3 = errorText.match(/\bB(\d+)\b/i);
          
          if (batchMatch) {
            batchNumber = batchMatch[1];
          } else if (batchMatch2) {
            batchNumber = batchMatch2[1];
          } else if (batchMatch3) {
            batchNumber = 'B' + batchMatch3[1];
          }
          
          const medicineMatch = errorText.match(/for\s+([A-Za-z0-9\s\-]+?)(?:\s+not|\s+expired|$)/i);
          if (medicineMatch) {
            medicineName = medicineMatch[1].trim();
          }
          
          // If we have batch info, format the message
          if (batchNumber && (errorText.toLowerCase().includes('not found') || 
              errorText.toLowerCase().includes('expired') ||
              errorText.toLowerCase().includes('batch') ||
              errorText.toLowerCase().includes('invalid') ||
              errorText.toLowerCase().includes('does not exist'))) {
            if (medicineName) {
              errorMessage = `Batch ${batchNumber} for ${medicineName} not found or expired.`;
            } else {
              // Try to find medicine from request items
              const matchingItem = items.find((item: any) => 
                item.batch_no === batchNumber || 
                item.batch_no?.toUpperCase() === batchNumber.toUpperCase() ||
                item.batch_no?.replace(/\s/g, '') === batchNumber.replace(/\s/g, '')
              );
              if (matchingItem) {
                errorMessage = `Batch ${batchNumber} for ${matchingItem.medicine_name} not found or expired.`;
              } else {
                errorMessage = `Batch ${batchNumber} not found or expired.`;
              }
            }
          }
        }
        
        // If we still don't have a formatted message, use the original with status
        const baseErrorMessage = batchNumber 
          ? errorMessage 
          : `Failed to submit sale (Status: ${response.status}): ${errorMessage}`;
        const fullErrorMessage = errorCode
          ? `[${errorCode}] ${baseErrorMessage}`
          : baseErrorMessage;
        
        // Display error message in the response message area
        setResponseMessage(fullErrorMessage);
        setShowResponseMessage(true);
        // Auto-hide after 8 seconds for errors
        setTimeout(() => {
          setShowResponseMessage(false);
        }, 8000);
        
        if (onSubmitError) {
          onSubmitError(fullErrorMessage);
        } else {
          alert(fullErrorMessage);
        }
        setIsSubmitting(false);
        return;
      }

      const result = await response.json();
      console.log('Sale submitted successfully:', result);
      console.log('API Response Message:', result.message);

      // Fetch full bill details after sale is generated
      let billApiData: any = null;
      const saleId =
        result?.sales_id ??
        result?.sale_id ??
        result?.id ??
        result?.sale?.id ??
        result?.saleId ??
        result?.salesId;

      if (saleId) {
        try {
          const billResponse = await apiGet(`sales/${saleId}/bill`);
          if (billResponse.ok) {
            billApiData = await billResponse.json();
            console.log('Bill details fetched successfully:', billApiData);
          } else {
            const errorText = await billResponse.text();
            console.error('Failed to fetch bill details:', billResponse.status, errorText);
          }
        } catch (billError) {
          console.error('Error fetching bill details:', billError);
        }
      } else {
        console.warn('Sale ID not found in sales response. Skipping bill fetch.');
      }
      
      const baseBillData = billApiData || result;

      // Store bill data from API response (include applied whole-bill discount amount so bill shows the entered discount)
      const generatedBillData = {
        ...baseBillData,
        customerName: baseBillData?.customer?.name || customerName.trim(),
        customerPhone: baseBillData?.customer?.phone || phoneNumber.trim() || '',
        paymentMode: baseBillData?.financial_summary?.payment_mode || normalizedPaymentMode,
        amountPaid: baseBillData?.financial_summary?.amount_paid ?? (parseFloat(String(amountPaid)) || 0),
        patientId: patientId,
        billItems: validRows,
        subtotal: subtotal,
        totalDiscount: totalDiscount,
        totalGST: totalGST,
        totalAmount: totalAmount,
        wholeBillDiscountPercent: wholeBillDiscount,
        wholeBillDiscountAmount: wholeBillDiscountAmount,
        grandTotalRounded: grandTotal
      };
      
      // Store bill data for popup display
      setBillData(generatedBillData);
      console.log('Generated Bill Data:', generatedBillData);
      
      // Extract message from response (check multiple possible fields)
      const apiMessage = result.message || result.response_message || result.note || null;
      
      // Display the API response message
      if (apiMessage) {
        setResponseMessage(apiMessage);
        setShowResponseMessage(true);
        // Auto-hide after 5 seconds
        setTimeout(() => {
          setShowResponseMessage(false);
        }, 5000);
      }
      
      const successMessage = `Sale submitted successfully! Bill generated for ${validRows.length} item(s).`;
      
      if (onSubmitSuccess) {
        onSubmitSuccess(successMessage);
      } else {
        alert(successMessage);
      }
      
      setIsSubmitting(false);
      
    } catch (error: any) {
      console.error('Error submitting sale:', error);
      
      let errorMessage = '';
      if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
        errorMessage = 'CORS Error: Unable to connect to the sales API. Please check your connection and try again.';
      } else {
        errorMessage = `Network error: ${error.message || 'Unknown error'}. Please check your connection and try again.`;
      }
      
      if (onSubmitError) {
        onSubmitError(errorMessage);
      } else {
        alert(errorMessage);
      }
      setIsSubmitting(false);
    }
  };

  const handlePrintBill = () => {
    if (!billPopupContentRef.current) {
      alert('Please open the bill preview before printing.');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=900,height=650');
    if (!printWindow) {
      alert('Pop-up blocked. Please allow pop-ups to print the bill.');
      return;
    }

    const title = fullName ? `${fullName} Bill` : (clinicInfo?.name ? `${clinicInfo.name} Bill` : 'Bill');

    const printStyles = `
      @page {
        margin: 8mm;
        size: A4;
      }
      body {
        font-family: 'Arial', 'Helvetica', sans-serif;
        margin: 0;
        padding: 0;
        color: #000000;
        background: #ffffff;
        font-size: 11px;
        line-height: 1.3;
      }
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }
      .bill-close-button,
      .print-button-container {
        display: none !important;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 10px;
        margin: 0;
      }
      th, td {
        border: 1px solid #000000;
        padding: 4px 3px;
        font-size: 10px;
        line-height: 1.2;
      }
      th {
        background: #f0f0f0 !important;
        font-weight: 600;
      }
      h2 {
        margin: 0.3rem 0;
        font-size: 1.3rem;
        line-height: 1.2;
      }
      h3 {
        margin: 0.2rem 0;
        font-size: 0.9rem;
      }
      p {
        margin: 0.2rem 0;
        font-size: 10px;
        line-height: 1.3;
      }
      div {
        page-break-inside: avoid;
      }
      @media print {
        body {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
      }
    `;

    const html = `<!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>${printStyles}</style>
        </head>
        <body>
          ${billPopupContentRef.current.innerHTML}
        </body>
      </html>`;

    // Write content and wait for it to render before printing
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    // Use a small timeout to ensure styles/content are applied before printing
    printWindow.focus();
    setTimeout(() => {
      try {
        printWindow.print();
      } catch (e) {
        console.error('Print failed:', e);
      }
    }, 300);
  };

  // Run presigned upload + S3 upload + send-whatsapp-document (shared after verification or after OTP). Expects PDF blob.
  const runPresignedAndSendWhatsApp = async (pdfBlob: Blob) => {
    const customerId = localStorage.getItem('userId') ?? '';
    const presignedBody = { customer_id: String(customerId), file_name: 'test.pdf' };
    console.log('[WhatsApp] Step 1 — API: sales/whatsapp/presigned-upload, request body:', presignedBody);
    const presignedResponse = await apiPost('sales/whatsapp/presigned-upload', presignedBody);
    if (!presignedResponse.ok) {
      const errorText = await presignedResponse.text();
      let errorMessage = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.detail || errorJson.error || errorJson.message || errorText;
      } catch (e) {}
      console.error('[WhatsApp] Step 1 — sales/whatsapp/presigned-upload failed:', presignedResponse.status, errorMessage);
      throw new Error(`Failed to get presigned URL: ${errorMessage}`);
    }
    const presignedData = await presignedResponse.json();
    const { upload_url, s3_key } = presignedData;
    console.log('[WhatsApp] Step 1 — sales/whatsapp/presigned-upload response:', { ...presignedData, upload_url: upload_url ? '(present)' : undefined });
    if (!upload_url || !s3_key) throw new Error('Invalid response from presigned upload API');

    console.log('[WhatsApp] Step 2 — S3 PUT upload (PDF), s3_key:', s3_key, 'blob size:', pdfBlob.size);
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: pdfBlob,
    });
    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('[WhatsApp] Step 2 — S3 upload failed:', uploadResponse.status, errorText || uploadResponse.statusText);
      throw new Error(`Failed to upload PDF to S3: ${uploadResponse.status} ${errorText || uploadResponse.statusText}`);
    }
    console.log('[WhatsApp] Step 2 — S3 upload success, status:', uploadResponse.status);

    // Prepare form-data for send-whatsapp-document API
    const formData = new FormData();
    // Format phone number with 91 prefix for WhatsApp API
    const rawPhone = phoneNumber.trim().replace(/\s+|[-()]/g, '');
    const formattedPhone = rawPhone.startsWith('91') ? rawPhone : `91${rawPhone}`;
    formData.append('to', formattedPhone);
    formData.append('uploaded_file_key', s3_key);
    formData.append('patient_name', customerName?.trim() || '');
    formData.append('customer_id', customerId);
    
    // Get pharma_id from localStorage
    let pharmaId = '';
    try {
      const pharmaStr = localStorage.getItem('pharmaDetails');
      if (pharmaStr) {
        const pharma = JSON.parse(pharmaStr);
        pharmaId = pharma.id || pharma.pharma_id || '';
      }
    } catch (_) {}
    formData.append('pharma_id', pharmaId);

    console.log('[WhatsApp] Step 3 — API: sales/send-whatsapp-document (FormData), uploaded_file_key:', s3_key, 'customer_id:', customerId);
    const sendUrl = getApiUrl('sales/send-whatsapp-document');
    const authToken = localStorage.getItem('authToken');
    const processResponse = await fetch(sendUrl, {
      method: 'POST',
      headers: {
        'ngrok-skip-browser-warning': 'true',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: formData,
    });
    if (!processResponse.ok) {
      const errorText = await processResponse.text();
      let errorMessage = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        const detail = errorJson.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: { loc?: string[]; msg?: string }) =>
            (err.loc && err.loc.length ? err.loc.join('.') + ': ' : '') + (err.msg || JSON.stringify(err))
          ).join('; ');
        } else {
          errorMessage = detail || errorJson.error || errorJson.message || errorText;
        }
      } catch (e) {}
      console.error('[WhatsApp] Step 3 — sales/send-whatsapp-document failed:', processResponse.status, errorMessage);
      throw new Error(`Failed to send to WhatsApp: ${errorMessage}`);
    }
    const processResult = await processResponse.json().catch(() => ({}));
    console.log('[WhatsApp] Step 3 — sales/send-whatsapp-document response:', processResult);
    console.log('[WhatsApp] Process complete — invoice sent to WhatsApp successfully');
    setResponseMessage('Invoice sent to WhatsApp successfully!');
    setShowResponseMessage(true);
    setTimeout(() => setShowResponseMessage(false), 5000);
  };

  const handleSendToWhatsApp = async () => {
    if (!billPopupContentRef.current) {
      alert('Please open the bill preview before sending to WhatsApp.');
      return;
    }

    if (!phoneNumber || phoneNumber.trim() === '') {
      alert('Phone number is required to send invoice to WhatsApp.');
      return;
    }

    console.log('[WhatsApp] Send to WhatsApp clicked — starting process');
    try {
      setIsSendingToWhatsApp(true);

      // Temporarily hide UI elements before capturing PDF
      const closeButton = document.querySelector('.bill-close-button') as HTMLElement;
      const printButtonContainer = document.querySelector('.print-button-container') as HTMLElement;
      if (closeButton) closeButton.style.display = 'none';
      if (printButtonContainer) printButtonContainer.style.display = 'none';

      // Capture the invoice as canvas then create PDF
      const canvas = await html2canvas(billPopupContentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: billPopupContentRef.current.scrollWidth,
        height: billPopupContentRef.current.scrollHeight,
      });

      // Restore UI elements after capture
      if (closeButton) closeButton.style.display = 'flex';
      if (printButtonContainer) printButtonContainer.style.display = 'flex';

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Create PDF with A4 dimensions (210mm x 297mm) for proper vertical format
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate dimensions to fit content within A4 page with margins
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // 10mm margins
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);
      
      // Calculate scaling to fit content
      const scaleX = contentWidth / (imgWidth * 0.264583); // Convert px to mm (1px = 0.264583mm)
      const scaleY = contentHeight / (imgHeight * 0.264583);
      const scale = Math.min(scaleX, scaleY);
      
      const scaledWidth = imgWidth * scale * 0.264583;
      const scaledHeight = imgHeight * scale * 0.264583;
      
      // Center the content on the page
      const x = (pageWidth - scaledWidth) / 2;
      const y = margin;
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      const pdfBlob = pdf.output('blob');

      (async () => {
        const blob = pdfBlob;
        if (!blob) {
          alert('Failed to capture invoice as PDF.');
          setIsSendingToWhatsApp(false);
          return;
        }
        console.log('[WhatsApp] Invoice captured as PDF, blob size:', blob.size, 'bytes');

        try {
          // Step 0: Check WhatsApp verification with new API
          const rawPhone = phoneNumber.trim().replace(/\s+|[-()]/g, '');
          const mobile = rawPhone.startsWith('91') ? rawPhone : (rawPhone.length <= 10 ? `91${rawPhone}` : rawPhone);
          
          console.log('[WhatsApp] Step 0 — API: whatsapp/verified-check, mobile:', mobile);
          const authToken = localStorage.getItem('authToken');
          const checkResponse = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/auth/whatsapp/verified-check', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
              ...(authToken && { Authorization: `Bearer ${authToken}` }),
            },
            body: JSON.stringify({ mobile }),
          });
          
          const checkData = checkResponse.ok ? await checkResponse.json().catch(() => ({})) : null;
          console.log('[WhatsApp] Step 0 — whatsapp/verified-check response:', checkData);
          
          const isVerified = checkData?.verified === true;

          if (isVerified) {
            console.log('[WhatsApp] Step 0 — phone number verified, proceeding with upload and send');
            await runPresignedAndSendWhatsApp(blob);
          } else {
            console.log('[WhatsApp] Step 0 — phone number not verified, requesting number verification');
            whatsAppPendingBlobRef.current = blob;
            
            // Get pharmacy details for verification
            let pharmaName = '';
            let pharmaId = '';
            try {
              const pharmaStr = localStorage.getItem('pharmaDetails');
              if (pharmaStr) {
                const pharma = JSON.parse(pharmaStr);
                pharmaName = pharma.pharmacy_name || pharma.name || '';
                pharmaId = pharma.id || pharma.pharma_id || '';
              }
            } catch (_) {}
            
            // Step 1: Request number verification
            const requestVerificationBody = {
              number: mobile,
              name: customerName?.trim() || '',
              is_pharma: true,
              pharma_name: pharmaName,
              pharma_id: pharmaId
            };
            
            console.log('[WhatsApp] Request number verification — API: number-verification, request body:', requestVerificationBody);
            const authToken = localStorage.getItem('authToken');
            const verificationResponse = await fetch('https://edit-agent-amd-0-1-15.onrender.com/real-agent/whatsapp/number-verification', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true',
                ...(authToken && { Authorization: `Bearer ${authToken}` }),
              },
              body: JSON.stringify(requestVerificationBody),
            });
            
            if (!verificationResponse.ok) {
              const errorText = await verificationResponse.text();
              console.error('[WhatsApp] Request number verification failed:', verificationResponse.status, errorText);
              throw new Error(`Failed to request number verification: ${errorText}`);
            }
            
            console.log('[WhatsApp] Number verification requested successfully, showing OTP modal');
            setShowOtpModal(true);
            setOtpValue('');
            setOtpError(null);
          }
        } catch (error: any) {
          console.error('Error in WhatsApp flow:', error);
          setResponseMessage(error?.message || 'Failed to verify number or send to WhatsApp.');
          setShowResponseMessage(true);
          setTimeout(() => setShowResponseMessage(false), 8000);
        } finally {
          setIsSendingToWhatsApp(false);
        }
      })();
    } catch (error: any) {
      console.error('Error capturing invoice:', error);
      alert('Failed to capture invoice as PDF. Please try again.');
      setIsSendingToWhatsApp(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResendingOtp(true);
    setOtpError(null);
    try {
      const rawPhone = phoneNumber.trim().replace(/\s+|[-()]/g, '');
      const mobile = rawPhone.startsWith('91') ? rawPhone : (rawPhone.length <= 10 ? `91${rawPhone}` : rawPhone);
      
      // Get pharmacy details for verification
      let pharmaName = '';
      let pharmaId = '';
      try {
        const pharmaStr = localStorage.getItem('pharmaDetails');
        if (pharmaStr) {
          const pharma = JSON.parse(pharmaStr);
          pharmaName = pharma.pharmacy_name || pharma.name || '';
          pharmaId = pharma.id || pharma.pharma_id || '';
        }
      } catch (_) {}
      
      // Request number verification again
      const requestVerificationBody = {
        number: mobile,
        name: customerName?.trim() || '',
        is_pharma: true,
        pharma_name: pharmaName,
        pharma_id: pharmaId
      };
      
      console.log('[WhatsApp] Resend OTP — API: number-verification, request body:', requestVerificationBody);
      const authToken = localStorage.getItem('authToken');
      const verificationResponse = await fetch('https://edit-agent-amd-0-1-15.onrender.com/real-agent/whatsapp/number-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
        body: JSON.stringify(requestVerificationBody),
      });
      
      if (!verificationResponse.ok) {
        const errorText = await verificationResponse.text();
        console.error('[WhatsApp] Resend OTP failed:', verificationResponse.status, errorText);
        setOtpError('Failed to resend OTP. Please try again.');
        return;
      }
      
      console.log('[WhatsApp] OTP resent successfully');
      setOtpError(null);
      // Optionally show success message
      setResponseMessage('OTP has been resent to your phone number.');
      setShowResponseMessage(true);
      setTimeout(() => setShowResponseMessage(false), 5000);
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      setOtpError(error?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleOtpSubmit = async () => {
    const otp = otpValue.trim();
    if (!otp) {
      setOtpError('Please enter the OTP.');
      return;
    }
    setIsVerifyingOtp(true);
    setOtpError(null);
    try {
      const rawPhone = phoneNumber.trim().replace(/\s+|[-()]/g, '');
      const number = rawPhone.startsWith('91') ? rawPhone : (rawPhone.length <= 10 ? `91${rawPhone}` : rawPhone);
      
      // Get pharmacy ID for verification
      let pharmaId = '';
      try {
        const pharmaStr = localStorage.getItem('pharmaDetails');
        if (pharmaStr) {
          const pharma = JSON.parse(pharmaStr);
          pharmaId = pharma.id || pharma.pharma_id || '';
        }
      } catch (_) {}
      
      const verifyBody = {
        number: number,
        otp: otp,
        is_pharma: true,
        pharma_id: pharmaId
      };
      
      const url = 'https://edit-agent-amd-0-1-15.onrender.com/real-agent/whatsapp/verify-otp';
      console.log('[WhatsApp] Verify OTP — API: verify-otp, request body:', { ...verifyBody, otp: '***' });
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(verifyBody),
      });
      
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.detail || data?.message || data?.error || res.statusText || 'Verification failed';
        setOtpError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        return;
      }
      
      console.log('[WhatsApp] OTP verification success');
      setShowOtpModal(false);
      setOtpValue('');
      const pendingBlob = whatsAppPendingBlobRef.current;
      whatsAppPendingBlobRef.current = null;
      
      if (pendingBlob) {
        setIsSendingToWhatsApp(true);
        try {
          await runPresignedAndSendWhatsApp(pendingBlob);
        } catch (err: any) {
          setResponseMessage(err?.message || 'Failed to send to WhatsApp.');
          setShowResponseMessage(true);
          setTimeout(() => setShowResponseMessage(false), 8000);
        } finally {
          setIsSendingToWhatsApp(false);
        }
      }
    } catch (err: any) {
      setOtpError(err?.message || 'Verification request failed.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      background: 'white',
      borderRadius: '0',
      padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(239, 68, 68, 0.1)',
      marginTop: '1rem',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      overflowY: 'visible'
    }}>
      {/* OTP Modal for WhatsApp number verification */}
      {showOtpModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10001,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }} onClick={() => {}}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            maxWidth: '360px',
            width: '100%',
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', color: '#111827' }}>Verify phone number</h3>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
              Enter the OTP sent to {phoneNumber.trim()}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter OTP"
                value={otpValue}
                onChange={e => { setOtpValue(e.target.value); setOtpError(null); }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `1px solid ${otpError ? '#ef4444' : '#d1d5db'}`,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={isResendingOtp}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  background: isResendingOtp ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  cursor: isResendingOtp ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                {isResendingOtp ? 'Resending...' : 'Resend OTP'}
              </button>
            </div>
            {otpError && <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8125rem', color: '#ef4444' }}>{otpError}</p>}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => { setShowOtpModal(false); setOtpValue(''); setOtpError(null); whatsAppPendingBlobRef.current = null; }}
                style={{
                  flex: 1,
                  padding: '0.625rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isVerifyingOtp}
                onClick={handleOtpSubmit}
                style={{
                  flex: 1,
                  padding: '0.625rem 1rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: isVerifyingOtp ? '#9ca3af' : '#10b981',
                  color: 'white',
                  cursor: isVerifyingOtp ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                {isVerifyingOtp ? 'Verifying…' : 'Verify & Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginBottom: '1.5rem',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        {/* Item Discount */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 0',
          borderBottom: '1px solid #f3f4f6',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          <span style={{
            fontSize: isMobile ? '0.9375rem' : '1rem',
            fontWeight: '600',
            color: '#374151',
            flexShrink: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            Total Value:
          </span>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '0.15rem',
            marginLeft: '1rem',
            flexShrink: 0,
            minWidth: isMobile ? '110px' : '140px',
          }}>
            <span style={{
              fontSize: isMobile ? '0.9375rem' : '1rem',
              fontWeight: '700',
              color: '#ef4444',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {formatCurrency(totalAmount)}
            </span>
            <span style={{
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              fontWeight: '600',
              color: '#6b7280',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              Item Discount: -{formatCurrency(totalDiscount)}
            </span>
          </div>
        </div>

        {/* Whole Bill Discount - Percentage and Rupee */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 0',
          borderBottom: '1px solid #f3f4f6',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          gap: '0.75rem',
          flexWrap: 'wrap'
        }}>
          <span style={{
            fontSize: isMobile ? '0.9375rem' : '1rem',
            fontWeight: '600',
            color: '#374151',
            flexShrink: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            Whole Bill Discount:
          </span>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
            flex: '0 0 auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="%"
                value={wholeBillDiscountInput}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const integerOnlyValue = rawValue.replace(/\D/g, '');
                  setWholeBillDiscountInput(integerOnlyValue);

                  if (integerOnlyValue === '') {
                    handleWholeBillDiscountChange(0);
                    setWholeBillDiscountRupeeInput('');
                    return;
                  }

                  const value = parseInt(integerOnlyValue, 10);
                  if (!isNaN(value)) {
                    const clampedValue = Math.min(Math.max(value, 0), 100);
                    handleWholeBillDiscountChange(clampedValue);
                    const rupeeDiscount = totalAmount > 0 ? (totalAmount * clampedValue) / 100 : 0;
                    setWholeBillDiscountRupeeInput(rupeeDiscount === 0 ? '' : rupeeDiscount.toFixed(2));
                  }
                }}
                style={{
                  width: isMobile ? '64px' : '72px',
                  padding: '0.5rem 0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  background: 'white',
                  color: '#111827',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  textAlign: 'right',
                  fontWeight: '600'
                }}
                onFocus={(e) => {
                  setIsEditingWholeBillDiscountPercent(true);
                  if (e.currentTarget) {
                    e.currentTarget.style.borderColor = '#ef4444';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  setIsEditingWholeBillDiscountPercent(false);
                  if (e.currentTarget) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                  if (wholeBillDiscountInput === '') {
                    handleWholeBillDiscountChange(0);
                    setWholeBillDiscountInput('');
                    return;
                  }

                  const value = parseInt(wholeBillDiscountInput, 10);
                  if (isNaN(value)) {
                    setWholeBillDiscountInput(
                      wholeBillDiscount === 0 ? '' : wholeBillDiscount.toString()
                    );
                    return;
                  }

                  const clampedValue = Math.min(Math.max(value, 0), 100);
                  handleWholeBillDiscountChange(clampedValue);
                  setWholeBillDiscountInput(clampedValue === 0 ? '' : clampedValue.toString());
                  const rupeeDiscount = totalAmount > 0 ? (totalAmount * clampedValue) / 100 : 0;
                  setWholeBillDiscountRupeeInput(rupeeDiscount === 0 ? '' : rupeeDiscount.toFixed(2));
                }}
              />
              <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '600' }}>%</span>
            </div>
            <span style={{ color: '#d1d5db', fontSize: '1rem' }}>or</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '600' }}>₹</span>
              <input
                type="text"
                inputMode="decimal"
                min="0"
                max={totalAmount}
                step="0.01"
                placeholder="0.00"
                value={wholeBillDiscountRupeeInput}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(rawValue)) {
                    setWholeBillDiscountRupeeInput(rawValue);

                    if (rawValue === '' || rawValue === '.' || totalAmount <= 0) {
                      handleWholeBillDiscountChange(0);
                      setWholeBillDiscountInput('');
                      return;
                    }

                    const value = parseFloat(rawValue);
                    if (!isNaN(value)) {
                      const rupeeDiscount = Math.min(Math.max(0, value), totalAmount);
                      const pct = (rupeeDiscount / totalAmount) * 100;
                      const precisePct = Math.min(Math.max(Number(pct.toFixed(4)), 0), 100);
                      handleWholeBillDiscountChange(precisePct);
                      const displayPct = Math.round(precisePct);
                      setWholeBillDiscountInput(displayPct === 0 ? '' : displayPct.toString());
                    }
                  }
                }}
                style={{
                  width: isMobile ? '80px' : '90px',
                  padding: '0.5rem 0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  background: 'white',
                  color: '#111827',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  textAlign: 'right',
                  fontWeight: '600'
                }}
                onFocus={(e) => {
                  setIsEditingWholeBillDiscountRupee(true);
                  if (e.currentTarget) {
                    e.currentTarget.style.borderColor = '#ef4444';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  setIsEditingWholeBillDiscountRupee(false);
                  if (e.currentTarget) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                  if (wholeBillDiscountRupeeInput === '' || wholeBillDiscountRupeeInput === '.' || totalAmount <= 0) {
                    setWholeBillDiscountRupeeInput('');
                    return;
                  }

                  const value = parseFloat(wholeBillDiscountRupeeInput);
                  if (isNaN(value)) {
                    setWholeBillDiscountRupeeInput('');
                    return;
                  }

                  const rupeeDiscount = Math.min(Math.max(0, value), totalAmount);
                  setWholeBillDiscountRupeeInput(rupeeDiscount === 0 ? '' : rupeeDiscount.toFixed(2));
                }}
              />
            </div>
            <span style={{
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              fontWeight: '700',
              color: '#ef4444',
              flexShrink: 0
            }}>
              -{formatCurrency(wholeBillDiscountAmount)}
            </span>
          </div>
        </div>

        {/* GST */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 0',
          borderBottom: '1px solid #f3f4f6',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          <span style={{
            fontSize: isMobile ? '0.9375rem' : '1rem',
            fontWeight: '600',
            color: '#374151',
            flexShrink: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            GST:
          </span>
          <span style={{
            fontSize: isMobile ? '0.9375rem' : '1rem',
            fontWeight: '700',
            color: '#10b981',
            flexShrink: 0,
            marginLeft: '1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            +{formatCurrency(totalGST)}
          </span>
        </div>

        {/* Grand Total */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 0',
          borderTop: '2px solid #e5e7eb',
          borderBottom: '2px solid #e5e7eb',
          marginTop: '0.5rem',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          <span style={{
            fontSize: isMobile ? '1.125rem' : '1.25rem',
            fontWeight: '700',
            color: '#1f2937',
            flexShrink: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            Grand Total:
          </span>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}>
            {grandTotal !== grandTotalUnrounded && (
              <span style={{
                fontSize: isMobile ? '0.875rem' : '1rem',
                fontWeight: '600',
                color: '#6b7280',
                marginBottom: '0.25rem'
              }}>
                Actual: {formatCurrency(grandTotalUnrounded)}
              </span>
            )}
            <span style={{
              fontSize: isMobile ? '1.125rem' : '1.25rem',
              fontWeight: '800',
              color: '#ef4444',
              flexShrink: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {formatCurrency(grandTotal)}
              {grandTotal !== grandTotalUnrounded && (
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginLeft: '0.35rem' }}>
                  (rounded up)
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Response Message Display */}
      {showResponseMessage && responseMessage && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem 1.5rem',
          background: responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
            ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
            : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
            ? '2px solid #ef4444'
            : '2px solid #f59e0b',
          borderRadius: '12px',
          boxShadow: responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
            ? '0 4px 12px rgba(239, 68, 68, 0.2)'
            : '0 4px 12px rgba(245, 158, 11, 0.2)',
          position: 'relative',
          overflow: 'hidden',
          animation: 'slideInDown 0.5s ease-out'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1
            }}>
              <div style={{
                background: responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
                  ? 'rgba(239, 68, 68, 0.2)'
                  : 'rgba(245, 158, 11, 0.2)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500') ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  fontWeight: '700',
                  color: responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
                    ? '#991b1b'
                    : '#92400e',
                  margin: '0 0 0.25rem 0'
                }}>
                  {responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
                    ? 'Error Message'
                    : 'Response Message'}
                </h3>
                <p style={{
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  color: responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
                    ? '#991b1b'
                    : '#92400e',
                  margin: 0,
                  fontWeight: '600',
                  lineHeight: '1.4',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}>
                  {responseMessage}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowResponseMessage(false);
                setResponseMessage(null);
              }}
              style={{
                background: responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
                  ? 'rgba(239, 68, 68, 0.2)'
                  : 'rgba(245, 158, 11, 0.2)',
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
                    ? 'rgba(239, 68, 68, 0.3)'
                    : 'rgba(245, 158, 11, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500')
                    ? 'rgba(239, 68, 68, 0.2)'
                    : 'rgba(245, 158, 11, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={responseMessage.includes('Failed') || responseMessage.includes('Error') || responseMessage.includes('500') ? '#991b1b' : '#92400e'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        flexDirection: isMobile ? 'column' : 'row',
        width: '100%'
      }}>
        {/* Submit Sale / Generate Bill Button */}
        <button
          onClick={() => {
            if (billData) {
              // If bill data exists, show the bill popup
              setShowBillPopup(true);
            } else {
              // Otherwise, submit the sale
              handleSubmitSale();
            }
          }}
          disabled={isSubmitDisabled}
          type="button"
          style={{
            flex: 1,
            background: isSubmitDisabled
              ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
              : billData
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: isMobile ? '0.875rem 1.5rem' : '1rem 2rem',
            fontSize: isMobile ? '0.9375rem' : '1rem',
            fontWeight: '600',
            cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            boxShadow: isSubmitDisabled
              ? '0 2px 4px rgba(0, 0, 0, 0.1)'
              : billData
              ? '0 4px 12px rgba(16, 185, 129, 0.3)'
              : '0 4px 12px rgba(239, 68, 68, 0.3)',
            outline: 'none',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitDisabled && e.currentTarget) {
              if (billData) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
              } else {
                e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
              }
              e.currentTarget.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitDisabled && e.currentTarget) {
              if (billData) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              } else {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
              }
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
          onFocus={(e) => {
            if (!isSubmitDisabled && e.currentTarget) {
              e.currentTarget.style.outline = billData 
                ? '2px solid rgba(16, 185, 129, 0.5)'
                : '2px solid rgba(239, 68, 68, 0.5)';
              e.currentTarget.style.outlineOffset = '2px';
            }
          }}
          onBlur={(e) => {
            if (e.currentTarget) {
              e.currentTarget.style.outline = 'none';
            }
          }}
        >
          {isSubmitting ? (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                <line x1="12" y1="2" x2="12" y2="6"/>
                <line x1="12" y1="18" x2="12" y2="22"/>
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
                <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                <line x1="2" y1="12" x2="6" y2="12"/>
                <line x1="18" y1="12" x2="22" y2="12"/>
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
                <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
              </svg>
              <span>Submitting...</span>
            </>
          ) : billData ? (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z"/>
              </svg>
              <span>Generate Bill</span>
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span>Submit Sale</span>
            </>
          )}
        </button>

        {/* Done Button */}
        <button
          onClick={() => {
            if (onDone) {
              onDone();
            }
          }}
          disabled={!billData}
          type="button"
          style={{
            flex: 1,
            background: !billData
              ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: isMobile ? '0.875rem 1.5rem' : '1rem 2rem',
            fontSize: isMobile ? '0.9375rem' : '1rem',
            fontWeight: '600',
            cursor: !billData ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            boxShadow: !billData
              ? '0 2px 4px rgba(0, 0, 0, 0.1)'
              : '0 4px 12px rgba(59, 130, 246, 0.3)',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onMouseEnter={(e) => {
            if (billData && e.currentTarget) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (billData && e.currentTarget) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }
          }}
          onFocus={(e) => {
            if (billData && e.currentTarget) {
              e.currentTarget.style.outline = '2px solid rgba(59, 130, 246, 0.5)';
              e.currentTarget.style.outlineOffset = '2px';
            }
          }}
          onBlur={(e) => {
            if (e.currentTarget) {
              e.currentTarget.style.outline = 'none';
            }
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"/>
          </svg>
          <span>Done</span>
        </button>
      </div>
      {/* Bill Popup Modal */}
      {showBillPopup && billData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'white',
            zIndex: 10000,
            width: '100%',
            height: '100%',
            overflow: 'auto',
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={() => setShowBillPopup(false)}
        >
          <div
            className="bill-popup-content"
            style={{
              background: 'white',
              width: '100%',
              maxWidth: '210mm',
              margin: '0 auto',
              minHeight: '100%',
              overflow: 'auto',
              position: 'relative',
              animation: 'slideInUp 0.3s ease-out',
              padding: '0'
            }}
            ref={billPopupContentRef}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="bill-close-button"
              onClick={() => setShowBillPopup(false)}
              style={{
                position: 'fixed',
                top: isMobile ? '1rem' : '1.5rem',
                right: isMobile ? '1rem' : '1.5rem',
                background: 'rgba(239, 68, 68, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 1)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <svg width={isMobile ? "20" : "24"} height={isMobile ? "20" : "24"} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            {/* Bill Content - Compact Professional Design */}
            <div style={{
              padding: isMobile ? '1rem 0.75rem' : '1.25rem 1.5rem',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              fontSize: '0.85rem',
              lineHeight: '1.4'
            }}>
              {/* Pharmacy Header - Compact Design */}
              <div style={{
                textAlign: 'center',
                marginBottom: '0.75rem'
              }}>
                <h2 style={{
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: '0 0 0.4rem 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  lineHeight: '1.2'
                }}>
                  {fullName || clinicInfo?.name || 'PHARMACY BILL'}
                </h2>
                {clinicInfo?.address && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#111827', 
                    lineHeight: 1.4,
                    marginBottom: '0.3rem'
                  }}>
                    {clinicInfo.address}
                  </div>
                )}
                {clinicInfo?.phone && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#111827',
                    marginBottom: '0'
                  }}>
                    Ph no:- {clinicInfo.phone}
                  </div>
                )}
              </div>

              {/* Key Identifiers Section - DL NO, Sale Bill, GST NO */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75rem',
                paddingBottom: '0.5rem',
                borderBottom: '2px solid #111827',
                fontSize: '0.75rem'
              }}>
                <div style={{ textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                  DL NO :- {dlNo || (clinicInfo as any)?.dl_no || (clinicInfo as any)?.drug_license || 'N/A'}
                </div>
                <div style={{ 
                  textAlign: 'center', 
                  fontWeight: '700', 
                  color: '#111827',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase'
                }}>
                  Sale Bill
                </div>
                <div style={{ textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                  GST NO :- {gstin || clinicInfo?.gstNumber || 'N/A'}
                </div>
              </div>

              {/* Patient and Bill Details - Compact Two Column Layout */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '1rem',
                marginBottom: '0.75rem',
                fontSize: '0.75rem',
                borderBottom: '1px solid #111827',
                paddingBottom: '0.5rem'
              }}>
                {/* Left Column - Patient Information */}
                <div>
                  <div style={{ marginBottom: '0' }}>
                    <strong>Name:</strong> {billData.customer?.name || billData.customerName || customerName || 'N/A'}
                  </div>
                </div>
                {/* Right Column - Bill Information */}
                <div>
                  <div style={{ marginBottom: '0.3rem' }}>
                    <strong>Bill No:</strong> {billData.invoice_no || billData.invoiceNo || billData.bill_id || 'N/A'}
                  </div>
                  <div style={{ marginBottom: '0.3rem' }}>
                    <strong>Mobile:</strong> {(billData.customer?.phone || billData.customerPhone || phoneNumber)
                      ? `+91 ${billData.customer?.phone || billData.customerPhone || phoneNumber}`
                      : 'N/A'}
                  </div>
                  <div style={{ marginBottom: '0' }}>
                    <strong>Date:</strong> {formatBillDate(billData.invoice_date || billData.created_at || billData.invoiceDate || billData.createdAt)}
                  </div>
                </div>
              </div>

              {/* Items Table - Compact Professional Design */}
              <div style={{
                marginBottom: '0.75rem',
                overflowX: 'auto'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.7rem',
                  fontFamily: 'inherit',
                  border: '1px solid #111827'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f9fafb',
                      color: '#111827',
                      fontWeight: '600',
                      borderBottom: '2px solid #111827'
                    }}>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'left', border: '1px solid #111827', fontSize: '0.7rem' }}>Particular (s)</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'center', border: '1px solid #111827', fontSize: '0.7rem' }}>Batch No</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'center', border: '1px solid #111827', fontSize: '0.7rem' }}>Exp Date</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', fontSize: '0.7rem' }}>Rate</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', fontSize: '0.7rem' }}>Qty</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', fontSize: '0.7rem' }}>Amt.(Rs)</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', fontSize: '0.7rem' }}>Gross(Rs)</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', fontSize: '0.7rem' }}>CGST(Rs)</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', fontSize: '0.7rem' }}>SGST(Rs)</th>
                      <th style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', fontSize: '0.7rem' }}>Final Amt.(Rs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billData.items && billData.items.map((item: any, index: number) => {
                      // Find corresponding row data for expiry date (fallback)
                      const correspondingRow = billData.billItems?.find((row: BillingRow) => 
                        row['Medicine Name'] === item.medicine_name && row['Batch No'] === item.batch_no
                      );
                      
                      const unitPrice = Number(item.unit_price ?? item.selling_price ?? 0);
                      const quantity = Number(item.quantity ?? 0);
                      const rateTimesQty = unitPrice * quantity;
                      const discountAmount = Number(item.line_discount_amount ?? item.discount_amount ?? 0);
                      const taxAmount = Number(item.line_tax_amount ?? item.tax_amount ?? 0);
                      const cgstAmount = Number(item.line_cgst_amount ?? item.cgst_amount ?? (taxAmount / 2));
                      const sgstAmount = Number(item.line_sgst_amount ?? item.sgst_amount ?? (taxAmount / 2));
                      const lineTotal = Number(
                        item.line_total ?? item.final_amount ?? (unitPrice * quantity - discountAmount + taxAmount)
                      );
                      
                      const grossAmount = Number(
                        item.line_total !== undefined || item.final_amount !== undefined || item.line_tax_amount !== undefined
                          ? lineTotal - (cgstAmount + sgstAmount)
                          : unitPrice * quantity - discountAmount
                      );
                      
                      return (
                        <tr key={index} style={{
                          backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                          borderBottom: '1px solid #111827'
                        }}>
                          <td style={{ padding: '0.4rem 0.3rem', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {item.medicine_name}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'center', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {item.batch_no || '—'}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'center', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {item.expiry_date ? formatExpiryDate(item.expiry_date) : (correspondingRow?.['Expiry Date'] ? formatExpiryDate(correspondingRow['Expiry Date']) : '—')}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {unitPrice.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {quantity}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {rateTimesQty.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {grossAmount.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {cgstAmount.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', color: '#111827', fontSize: '0.7rem' }}>
                            {sgstAmount.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.4rem 0.3rem', textAlign: 'right', border: '1px solid #111827', color: '#111827', fontWeight: '600', fontSize: '0.7rem' }}>
                            {lineTotal.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Financial Summary Section - Matching API Response */}
              {(() => {
                const items = billData.items || [];
                const summary = billData.financial_summary || {};
                
                const computed = items.reduce((acc: any, item: any) => {
                  const unitPrice = Number(item.unit_price ?? item.selling_price ?? 0);
                  const quantity = Number(item.quantity ?? 0);
                  const discountAmount = Number(item.line_discount_amount ?? item.discount_amount ?? 0);
                  const taxAmount = Number(item.line_tax_amount ?? item.tax_amount ?? 0);
                  const lineTotal = Number(
                    item.line_total ?? item.final_amount ?? (unitPrice * quantity - discountAmount + taxAmount)
                  );
                  const lineSubtotal = lineTotal - taxAmount;
                  
                  acc.subtotal += lineSubtotal;
                  acc.lineDiscount += discountAmount;
                  acc.lineTax += taxAmount;
                  acc.total += lineTotal;
                  return acc;
                }, {
                  subtotal: 0,
                  lineDiscount: 0,
                  lineTax: 0,
                  total: 0
                });
                
                const subtotalWithoutTax = Number(summary.subtotal ?? computed.subtotal ?? 0);

                const lineDiscountAmount = Number(summary.line_discount_amount ?? computed.lineDiscount ?? 0);
                const lineTaxAmount = summary.line_tax_amount ?? computed.lineTax;
                const overallTaxAmount = Number(summary.overall_tax_amount ?? lineTaxAmount ?? 0);
                const overallCgstAmount = Number(summary.overall_cgst_amount ?? 0);
                const overallSgstAmount = Number(summary.overall_sgst_amount ?? 0);
                const grossBeforeDiscountFromItems = items.reduce(
                  (acc: number, item: any) =>
                    acc +
                    Number(item.unit_price ?? item.selling_price ?? 0) * Number(item.quantity ?? 0),
                  0
                );
                const subtotalAmount = Number(
                  summary.subtotal_amount ??
                    (grossBeforeDiscountFromItems > 0
                      ? grossBeforeDiscountFromItems
                      : summary.subtotal ?? computed.subtotal)
                );
                const totalAmount = Number(summary.total_amount ?? summary.total ?? computed.total ?? 0);
                const amountPaid = Number(summary.amount_paid ?? billData.amountPaid ?? 0);
                const balanceDue = Number(summary.balance_due ?? Math.max(totalAmount - amountPaid, 0));
                const paymentMode = summary.payment_mode ?? billData.paymentMode ?? 'cash';
                const status = summary.status ?? billData.status ?? 'completed';
                
                return (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '0.5rem'
                  }}>
                    <div style={{
                      width: isMobile ? '100%' : '280px',
                      fontSize: '0.7rem',
                      border: '1px solid #111827',
                      padding: '0.6rem',
                      background: '#f9fafb'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span><strong>Subtotal Amount:</strong></span>
                        <span><strong>₹{subtotalAmount.toFixed(2)}</strong></span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span><strong>Discount:</strong></span>
                        <span><strong>₹{lineDiscountAmount.toFixed(2)}</strong></span>
                      </div>
                      <div style={{
                        marginTop: '0.35rem',
                        marginBottom: '0.35rem',
                        padding: '0.45rem 0.4rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        background: '#f3f4f6'
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.35rem', fontSize: '0.68rem', color: '#374151' }}>
                          Tax summary
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span><strong>Subtotal (without tax):</strong></span>
                          <span><strong>₹{subtotalWithoutTax.toFixed(2)}</strong></span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span><strong>Overall Tax Amount:</strong></span>
                          <span><strong>₹{overallTaxAmount.toFixed(2)}</strong></span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span><strong>Overall CGST Amount:</strong></span>
                          <span><strong>₹{overallCgstAmount.toFixed(2)}</strong></span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span><strong>Overall SGST Amount:</strong></span>
                          <span><strong>₹{overallSgstAmount.toFixed(2)}</strong></span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', borderTop: '1px solid #111827', paddingTop: '0.35rem' }}>
                        <span><strong>Grand Total:</strong></span>
                        <span><strong>₹{totalAmount.toFixed(2)}</strong></span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span><strong>Paid Amount:</strong></span>
                        <span><strong>₹{amountPaid.toFixed(2)}</strong></span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                        <span><strong>Due:</strong></span>
                        <span><strong>₹{balanceDue.toFixed(2)}</strong></span>
                      </div>
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #111827' }}>
                        <div style={{ marginBottom: '0.3rem', fontSize: '0.7rem' }}>
                          <strong>Paid Amount (In Words):</strong>
                        </div>
                        <div style={{ fontSize: '0.65rem', fontStyle: 'italic', textTransform: 'uppercase', lineHeight: '1.3' }}>
                          {numberToWords(amountPaid)}
                        </div>
                      </div>
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #111827' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span><strong>Pay Mode:</strong></span>
                          <span><strong>{String(paymentMode || 'Cash').toUpperCase()}</strong></span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.3rem' }}>
                          <span><strong>Status:</strong></span>
                          <span><strong>{String(status || 'completed').toUpperCase()}</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Footer - Compact Professional Design */}
              <div style={{
                marginTop: '1rem',
                fontSize: '0.7rem',
                color: '#111827',
                borderTop: '1px solid #111827',
                paddingTop: '0.6rem'
              }}>
                <div style={{ marginBottom: '0.4rem', fontWeight: '600', fontSize: '0.7rem' }}>
                  Note: Fridge items once sold cannot be taken back.
                </div>
                <div style={{
                  marginTop: '0.65rem',
                  padding: '0.55rem 0.6rem',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.65rem',
                  lineHeight: 1.45,
                  color: '#374151'
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '0.35rem', color: '#111827', fontSize: '0.68rem' }}>
                    Column definitions (line items)
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                    <li style={{ marginBottom: '0.25rem' }}>
                      <strong>Amt. (Rs)</strong> — &quot;Amount&quot;: rate × quantity. Shown <em>before</em> any line discount.
                    </li>
                    <li style={{ marginBottom: '0.25rem' }}>
                      <strong>Final Amt. (Rs)</strong> — Line total <em>after</em> discount is applied and <em>after</em> CGST/SGST (the full payable amount for that line on this bill).
                    </li>
                    <li style={{ marginBottom: '0.25rem' }}>
                      <strong>Gross (Rs)</strong> — Value <em>after</em> discount and <em>before</em> tax (pre‑tax line amount).
                    </li>
                    <li>
                      <strong>Tax</strong> — CGST/SGST is calculated on the post‑discount amount (the same basis as <strong>Gross</strong>), not on undiscounted <strong>Amt.</strong>
                    </li>
                  </ul>
                </div>
                {fullName && (
                  <div style={{ marginTop: '0.6rem', fontSize: '0.7rem' }}>
                    <strong>Printed By :</strong> {fullName}
                  </div>
                )}
              </div>

              {/* Print Button */}
              <div className="print-button-container" style={{
                marginTop: '2rem',
                display: 'flex',
                justifyContent: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={handlePrintBill}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9H4C3.44772 9 3 9.44772 3 10V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V10C21 9.44772 20.5523 9 20 9H18M6 9V7C6 5.34315 7.34315 4 9 4H15C16.6569 4 18 5.34315 18 7V9M6 9H18M9 13H15"/>
                  </svg>
                  Print Bill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes slideInDown {
          0% {
            transform: translateY(-20px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideInUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default BillingSummary;
